import { base64ToBytes, bytesToBase64 } from '@casa/util';
import {
    DEFAULT_HTTP_TIMEOUT_MS,
    FrameType,
    HOST_EVENT_CHANNEL,
    MAX_BROWSE_MS,
    WsMessageKind,
    decodeFrame,
    encodeFrame,
    isValidStoragePath,
    isValidStoragePrefix,
    type BridgeRequest,
    type BridgeResults,
    type ChannelEvent,
    type ErrorPayload,
    type HostEvent,
    type SensorEvent,
    type HttpResponseHead,
    type OpenRequest,
    type WsOpenResponse,
} from './protocol.ts';
import type { Bridge, DeviceSensor, HttpRequest, HttpResponse, SensorReading, StorageBridge, TcpChannel, Unsubscribe, WsChannel, WsMessage } from './types.ts';

/** How long `net.browse` looks when the caller does not say. */
const DEFAULT_BROWSE_MS = 3_000;

// Not part of the ES2023 lib, but present in every runtime the client runs in (WebView main
// thread, Node tests). Drivers never get the client, so QuickJS does not need them.
declare const TextEncoder: new () => { encode(text: string) : Uint8Array };
declare const TextDecoder: new () => { decode(bytes: Uint8Array) : string };
declare const setTimeout: (callback: () => void, ms: number) => unknown;
declare const clearTimeout: (timer: unknown) => void;
declare const queueMicrotask: (callback: () => void) => void;

/** What a shell has to offer the app: a way to exchange whole encoded frames. */
export interface FrameTransport {
    post(frame: Uint8Array) : void;
    onFrame(listener: (frame: Uint8Array) => void) : Unsubscribe;
    /**
     * For a transport that can lose its host (the companion server's WebSocket): called when the
     * connection is lost. The host has closed every channel by then, so the client fails what was
     * open or waiting. Frames posted afterwards go to the next connection. The shells never call it.
     */
    onReset?(listener: (reason: string) => void) : Unsubscribe;
}

export interface BridgeClientOptions {
    /** Receives protocol problems (malformed or unexpected frames). Default: ignored. */
    log?: (message: string) => void;
    /**
     * How long an open may stay unanswered. A shell drops frames it does not trust without
     * replying, so this is what ends such an open. Default 15 s (shells give up connecting at 10 s).
     */
    openTimeoutMs?: number;
    /** How long a request (`storage.*`, `device.info`) may stay unanswered. Default 15 s. */
    requestTimeoutMs?: number;
}

/** A send is split into Data frames of at most this size; each waits for its `written` event. */
export const TCP_WRITE_CHUNK_BYTES = 64 * 1024;

/**
 * How much longer than its own `timeoutMs` an HTTP request may take before the client gives up on
 * the shell. The shell enforces the timeout; this only ends a request whose shell went silent.
 */
export const HTTP_TIMEOUT_GRACE_MS = 5_000;

const EMPTY = new Uint8Array();
const MAX_CHANNEL_ID = 0xffff_ffff;

/** What the frame dispatcher needs from an open channel, whatever its kind. */
interface Channel {
    data(payload: Uint8Array) : void;
    written() : void;
    closed(reason: string | undefined) : void;
}

interface PendingOpen {
    resolve(payload: Uint8Array) : void;
    reject(error: Error) : void;
}

/** Reads a response body to the end, into one array. */
export async function readAllBytes(body: AsyncIterable<Uint8Array>) : Promise<Uint8Array> {
    const chunks: Uint8Array[] = [];
    let length = 0;
    for await (const chunk of body) {
        chunks.push(chunk);
        length += chunk.length;
    }

    const all = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) {
        all.set(chunk, offset);
        offset += chunk.length;
    }

    return all;
}

/**
 * The body of an HTTP response: chunks pushed by the dispatcher, read once as an async iterable.
 * Leaving the iteration early (break, throw) cancels the request through `cancel`.
 */
function createBodyQueue(cancel: () => void) {
    const chunks: Uint8Array[] = [];
    let ended = false;
    let failure: Error | undefined;
    let wake: (() => void) | undefined;

    const notify = () : void => {
        const waiting = wake;
        wake = undefined;
        waiting?.();
    };

    return {
        push(chunk: Uint8Array) : void {
            if (ended) {
                return;
            }

            chunks.push(chunk);
            notify();
        },
        end(error?: Error) : void {
            if (ended) {
                return;
            }

            ended = true;
            failure = error;
            notify();
        },
        /**
         * Not a generator: a generator's `return` waits for a `next` that awaits a piece, so a reader
         * that stops while waiting (an event stream, hue-driver HU-11a) would cancel only once the next
         * piece came. Here `return` cancels at once, and the waiting `next` ends.
         */
        iterate() : AsyncIterableIterator<Uint8Array> {
            let stopped = false;
            const stop = () : void => {
                if (!ended) {
                    ended = true;
                    cancel();
                }
            };
            const iterator: AsyncIterableIterator<Uint8Array> = {
                [Symbol.asyncIterator]: () => iterator,
                async next() : Promise<IteratorResult<Uint8Array>> {
                    while (!stopped) {
                        const chunk = chunks.shift();
                        if (chunk) {
                            return { done: false, value: chunk };
                        }
                        if (failure) {
                            stopped = true;
                            throw failure;
                        }
                        if (ended) {
                            break;
                        }

                        await new Promise<void>((resolve) => {
                            wake = resolve;
                        });
                    }
                    stopped = true;

                    return { done: true, value: undefined };
                },
                async return() : Promise<IteratorResult<Uint8Array>> {
                    stopped = true;
                    chunks.length = 0;
                    stop();
                    notify();

                    return { done: true, value: undefined };
                },
            };

            return iterator;
        },
    };
}

/**
 * The app side of the native bridge: turns a frame transport into the `Bridge` interface.
 * Implemented: `net.*`, `storage`, `device` but `fullscreen`, `lifecycle`. The vault (deferred, decision §12)
 * and `device.fullscreen` (Phase 4) fail with "not implemented".
 */
export function createBridgeClient(
    transport: FrameTransport,
    options: BridgeClientOptions = {},
) : Bridge {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    const log = options.log ?? (() => {});
    const channels = new Map<number, Channel>();
    const pendingOpens = new Map<number, PendingOpen>();
    let lastChannelId = 0;

    function post(type: FrameType, channelId: number, payload: Uint8Array = EMPTY) : void {
        transport.post(encodeFrame({ type, channelId, payload }));
    }

    function allocateChannelId() : number {
        do {
            lastChannelId = lastChannelId >= MAX_CHANNEL_ID ? 1 : lastChannelId + 1;
        } while (channels.has(lastChannelId) || pendingOpens.has(lastChannelId)) {
            ;
        }
        return lastChannelId;
    }

    const foregroundListeners = new Set<() => void>();
    const backgroundListeners = new Set<() => void>();
    const sensorListeners: Record<DeviceSensor, Set<(reading: SensorReading) => void>> = { proximity: new Set(), light: new Set() };

    function sensorReading(event: Record<string, unknown>) : SensorReading | undefined {
        if (event.sensor === 'proximity' && typeof event.near === 'boolean') {
            return { sensor: 'proximity', near: event.near };
        }
        if (event.sensor === 'light' && typeof event.lux === 'number' && Number.isFinite(event.lux)) {
            return { sensor: 'light', lux: event.lux };
        }

        return undefined;
    }

    function hostEvent(payload: Uint8Array) : void {
        const event = readJson(payload) as (Partial<HostEvent | SensorEvent> & Record<string, unknown>) | undefined;
        if (event?.type === 'sensor') {
            const reading = sensorReading(event);
            if (!reading) {
                log('bridge: a sensor event without a reading');

                return;
            }
            for (const listener of [...sensorListeners[reading.sensor]]) {
                listener(reading);
            }

            return;
        }
        if (event?.type !== 'lifecycle' || (event.state !== 'foreground' && event.state !== 'background')) {
            log('bridge: unknown host event');

            return;
        }

        for (const listener of [...(event.state === 'foreground' ? foregroundListeners : backgroundListeners)]) {
            listener();
        }
    }

    function readJson(payload: Uint8Array) : unknown {
        try {
            return JSON.parse(decoder.decode(payload));
        } catch {
            return undefined;
        }
    }

    function createTcpChannel(id: number) : Channel & { api: TcpChannel } {
        const dataListeners = new Set<(data: Uint8Array) => void>();
        const closeListeners = new Set<(reason?: string) => void>();
        const writes: { resolve() : void; reject(error: Error) : void }[] = [];
        const closeWaiters: (() => void)[] = [];
        // Data that arrives before the first listener is attached is kept, not dropped.
        let buffered: Uint8Array[] = [];
        let closed = false;
        let closeReason: string | undefined;
        let sendTail: Promise<void> = Promise.resolve();

        async function sendChunks(data: Uint8Array) : Promise<void> {
            for (let offset = 0; offset < data.length; offset += TCP_WRITE_CHUNK_BYTES) {
                if (closed) {
                    throw new Error('channel closed');
                }

                const chunk = data.subarray(offset, offset + TCP_WRITE_CHUNK_BYTES);
                await new Promise<void>((resolve, reject) => {
                    writes.push({ resolve, reject });
                    post(FrameType.Data, id, chunk);
                });
            }
        }

        const api: TcpChannel = {
            send(data) {
                // Sends are serialised so the chunks of two calls cannot interleave.
                const previous = sendTail;
                const result = (async () : Promise<void> => {
                    await previous;
                    await sendChunks(data);
                })();

                // The next send waits for this one whether it fails or not; its caller sees the failure.
                sendTail = (async () : Promise<void> => {
                    try {
                        await result;
                    } catch {
                        // Reported to the caller of this send through `result`.
                    }
                })();

                return result;
            },
            close() {
                if (closed) {
                    return Promise.resolve();
                }

                return new Promise<void>((resolve) => {
                    closeWaiters.push(resolve);
                    post(FrameType.Close, id);
                });
            },
            onData(listener) {
                dataListeners.add(listener);
                if (buffered.length > 0) {
                    queueMicrotask(() => {
                        const pending = buffered;
                        buffered = [];
                        for (const chunk of pending) {
                            for (const dataListener of [...dataListeners]) {
                                dataListener(chunk);
                            }
                        }
                    });
                }
                return () => dataListeners.delete(listener);
            },
            onClose(listener) {
                closeListeners.add(listener);
                if (closed) {
                    queueMicrotask(() => listener(closeReason));
                }
                return () => closeListeners.delete(listener);
            },
        };

        return {
            api,
            data(payload) {
                if (dataListeners.size === 0 || buffered.length > 0) {
                    buffered.push(payload);
                } else {
                    for (const listener of [...dataListeners]) {
                        listener(payload);
                    }
                }
            },
            written() {
                writes.shift()?.resolve();
            },
            closed(reason) {
                closed = true;
                closeReason = reason;
                channels.delete(id);
                const error = new Error(reason ? `channel closed: ${reason}` : 'channel closed');
                for (const write of writes.splice(0)) {
                    write.reject(error);
                }
                for (const listener of [...closeListeners]) {
                    listener(reason);
                }
                for (const waiter of closeWaiters.splice(0)) {
                    waiter();
                }
            },
        };
    }

    // The host lost the connection and every channel with it: nothing open or waiting will be answered.
    transport.onReset?.((reason) => {
        const error = new Error(`bridge: connection to the host lost (${reason})`);
        for (const [id, open] of [...pendingOpens]) {
            pendingOpens.delete(id);
            open.reject(error);
        }
        for (const channel of [...channels.values()]) {
            channel.closed(`connection to the host lost (${reason})`);
        }
    });

    transport.onFrame((bytes) => {
        let frame;
        try {
            frame = decodeFrame(bytes);
        } catch (error) {
            log(`bridge: dropped malformed frame (${String(error)})`);

            return;
        }
        const id = frame.channelId;
        switch (frame.type) {
            case FrameType.Response: {
                const open = pendingOpens.get(id);
                pendingOpens.delete(id);
                if (open) {
                    open.resolve(frame.payload);
                } else {
                    log(`bridge: response for unknown channel ${id}`);
                }
                return;
            }
            case FrameType.Error: {
                const open = pendingOpens.get(id);
                pendingOpens.delete(id);
                const message = (readJson(frame.payload) as Partial<ErrorPayload> | undefined)?.message;
                if (open) {
                    open.reject(new Error(typeof message === 'string' ? message : 'bridge error'));
                } else {
                    log(`bridge: error for unknown channel ${id}: ${String(message)}`);
                }
                return;
            }
            case FrameType.Data:
                channels.get(id)?.data(frame.payload);

                return;
            case FrameType.Event: {
                if (id === HOST_EVENT_CHANNEL) {
                    return hostEvent(frame.payload);
                }

                const event = readJson(frame.payload) as Partial<ChannelEvent> | undefined;
                if (event?.type === 'written') {
                    channels.get(id)?.written();
                } else {
                    log(`bridge: unknown event on channel ${id}`);
                }
                return;
            }
            case FrameType.Close: {
                const reason = frame.payload.length > 0 ? decoder.decode(frame.payload) : undefined;
                channels.get(id)?.closed(reason);

                return;
            }
            default:
                log(`bridge: unexpected frame type ${frame.type}`);
        }
    });

    /**
     * Sends an open request and resolves with the payload of the shell's Response frame. The channel
     * is registered before the shell answers, so data that follows the answer at once is not lost.
     */
    function openChannel(id: number, channel: Channel, request: OpenRequest, timeoutMs: number) : Promise<Uint8Array> {
        channels.set(id, channel);

        return new Promise<Uint8Array>((resolve, reject) => {
            const timer = setTimeout(() => {
                pendingOpens.delete(id);
                fail(new Error('bridge: the shell did not answer the open request'));
            }, timeoutMs);
            const fail = (error: Error) : void => {
                clearTimeout(timer);
                channels.delete(id);
                reject(error);
            };
            pendingOpens.set(id, {
                resolve: (payload) => {
                    clearTimeout(timer);
                    resolve(payload);
                },
                reject: fail,
            });
            try {
                post(FrameType.Open, id, encoder.encode(JSON.stringify(request)));
            } catch (error) {
                pendingOpens.delete(id);
                fail(error instanceof Error ? error : new Error(String(error)));
            }
        });
    }

    async function openTcp(host: string, port: number) : Promise<TcpChannel> {
        const id = allocateChannelId();
        const channel = createTcpChannel(id);
        await openChannel(id, channel, { kind: 'tcp', host, port }, options.openTimeoutMs ?? 15_000);

        return channel.api;
    }

    async function httpRequest(request: HttpRequest) : Promise<HttpResponse> {
        if (request.tls && !request.url.toLowerCase().startsWith('https:')) {
            throw new Error('bridge: tls is for https: requests only');
        }
        if (request.tls && request.tls.ca.length === 0) {
            throw new Error('bridge: tls.ca names no authority');
        }

        const id = allocateChannelId();
        const timeoutMs = request.timeoutMs ?? DEFAULT_HTTP_TIMEOUT_MS;
        const body = createBodyQueue(() => {
            if (channels.has(id)) {
                post(FrameType.Close, id);
            }
        });
        const channel: Channel = {
            data: (payload) => body.push(payload),
            written: () => log(`bridge: unexpected written event on http channel ${id}`),
            closed: (reason) => {
                channels.delete(id);
                clearTimeout(deadline);
                body.end(reason ? new Error(`bridge: response cut off: ${reason}`) : undefined);
            },
        };

        // The backstop for a shell that stops answering mid-body; the shell's own timeout comes first.
        const deadline = setTimeout(() => {
            if (channels.delete(id)) {
                post(FrameType.Close, id);
                body.end(new Error(`bridge: no complete response after ${timeoutMs} ms`));
            }
        }, timeoutMs + HTTP_TIMEOUT_GRACE_MS);

        let headPayload: Uint8Array;
        try {
            headPayload = await openChannel(
                id,
                channel,
                {
                    kind: 'http',
                    method: request.method,
                    url: request.url,
                    headers: request.headers ?? {},
                    ...(request.body ? { body: bytesToBase64(request.body) } : {}),
                    timeoutMs,
                    ...(request.redirect === 'manual' ? { redirect: 'manual' as const } : {}),
                    ...(request.tls ? { tls: request.tls } : {}),
                },
                timeoutMs + HTTP_TIMEOUT_GRACE_MS,
            );
        } catch (error) {
            clearTimeout(deadline);
            throw error;
        }

        const head = readJson(headPayload) as Partial<HttpResponseHead> | undefined;
        if (typeof head?.status !== 'number' || typeof head.headers !== 'object' || head.headers === null) {
            post(FrameType.Close, id);
            channel.closed('malformed response head');
            throw new Error('bridge: malformed http response head');
        }

        return { status: head.status, headers: head.headers, body: body.iterate() };
    }

    /** Sends a request and resolves with the shell's result; rejects with its error or on timeout. */
    function sendRequest<Op extends BridgeRequest['op']>(request: Extract<BridgeRequest, { op: Op }>) : Promise<BridgeResults[Op]> {
        const id = allocateChannelId();

        return new Promise<BridgeResults[Op]>((resolve, reject) => {
            const timer = setTimeout(() => {
                pendingOpens.delete(id);
                reject(new Error(`bridge: the shell did not answer ${request.op}`));
            }, options.requestTimeoutMs ?? 15_000);
            pendingOpens.set(id, {
                resolve: (payload) => {
                    clearTimeout(timer);
                    const result = readJson(payload);
                    if (typeof result === 'object' && result !== null) {
                        resolve(result as BridgeResults[Op]);
                    } else {
                        reject(new Error(`bridge: malformed answer to ${request.op}`));
                    }
                },
                reject: (error) => {
                    clearTimeout(timer);
                    reject(error);
                },
            });
            try {
                post(FrameType.Request, id, encoder.encode(JSON.stringify(request)));
            } catch (error) {
                clearTimeout(timer);
                pendingOpens.delete(id);
                reject(error instanceof Error ? error : new Error(String(error)));
            }
        });
    }

    function checkPath(path: string) : void {
        if (!isValidStoragePath(path)) {
            throw new Error(`bridge: invalid storage path ${JSON.stringify(path)}`);
        }
    }

    const storage: StorageBridge = {
        async read(path) {
            checkPath(path);
            const { data } = await sendRequest({ op: 'storage.read', path });

            return data === null ? null : base64ToBytes(data);
        },
        async write(path, data) {
            checkPath(path);
            await sendRequest({ op: 'storage.write', path, data: bytesToBase64(data) });
        },
        async list(prefix) {
            if (!isValidStoragePrefix(prefix)) {
                throw new Error(`bridge: invalid storage prefix ${JSON.stringify(prefix)}`);
            }

            return (await sendRequest({ op: 'storage.list', prefix })).paths;
        },
        async remove(path) {
            checkPath(path);
            await sendRequest({ op: 'storage.remove', path });
        },
    };

    function createWsChannel(id: number) : Channel & { api: WsChannel; opened(protocol: string) : void } {
        const messageListeners = new Set<(message: WsMessage) => void>();
        const closeListeners = new Set<(reason?: string) => void>();
        const writes: { resolve() : void; reject(error: Error) : void }[] = [];
        const closeWaiters: (() => void)[] = [];
        // Messages that arrive before the first listener is attached are kept, not dropped.
        let buffered: WsMessage[] = [];
        let closed = false;
        let closeReason: string | undefined;
        let protocol = '';

        const deliver = (message: WsMessage) : void => {
            for (const listener of [...messageListeners]) {
                listener(message);
            }
        };

        const api: WsChannel = {
            get protocol() {
                return protocol;
            },
            send(message) {
                if (closed) {
                    return Promise.reject(new Error('channel closed'));
                }

                const isText = typeof message === 'string';
                const bytes = isText ? encoder.encode(message) : message;
                const payload = new Uint8Array(bytes.length + 1);
                payload[0] = isText ? WsMessageKind.Text : WsMessageKind.Binary;
                payload.set(bytes, 1);

                return new Promise<void>((resolve, reject) => {
                    writes.push({ resolve, reject });
                    try {
                        post(FrameType.Data, id, payload);
                    } catch (error) {
                        writes.pop();
                        reject(error instanceof Error ? error : new Error(String(error)));
                    }
                });
            },
            close() {
                if (closed) {
                    return Promise.resolve();
                }

                return new Promise<void>((resolve) => {
                    closeWaiters.push(resolve);
                    post(FrameType.Close, id);
                });
            },
            onMessage(listener) {
                messageListeners.add(listener);
                if (buffered.length > 0) {
                    queueMicrotask(() => {
                        const pendingMessages = buffered;
                        buffered = [];
                        for (const message of pendingMessages) {
                            deliver(message);
                        }
                    });
                }
                return () => messageListeners.delete(listener);
            },
            onClose(listener) {
                closeListeners.add(listener);
                if (closed) {
                    queueMicrotask(() => listener(closeReason));
                }
                return () => closeListeners.delete(listener);
            },
        };

        return {
            api,
            opened(chosen) {
                protocol = chosen;
            },
            data(payload) {
                const kind = payload[0];
                const body = payload.subarray(1);
                if (kind !== WsMessageKind.Text && kind !== WsMessageKind.Binary) {
                    log(`bridge: ws message of unknown kind on channel ${id}`);

                    return;
                }

                const message: WsMessage = kind === WsMessageKind.Text ? decoder.decode(body) : body.slice();
                if (messageListeners.size === 0 || buffered.length > 0) {
                    buffered.push(message);
                } else {
                    deliver(message);
                }
            },
            written() {
                writes.shift()?.resolve();
            },
            closed(reason) {
                closed = true;
                closeReason = reason;
                channels.delete(id);
                const error = new Error(reason ? `channel closed: ${reason}` : 'channel closed');
                for (const write of writes.splice(0)) {
                    write.reject(error);
                }
                for (const listener of [...closeListeners]) {
                    listener(reason);
                }
                for (const waiter of closeWaiters.splice(0)) {
                    waiter();
                }
            },
        };
    }

    async function openWs(url: string, opts: { headers?: Record<string, string>; protocols?: string[] } = {}) : Promise<WsChannel> {
        const id = allocateChannelId();
        const channel = createWsChannel(id);
        const answer = await openChannel(
            id,
            channel,
            { kind: 'ws', url, headers: opts.headers ?? {}, protocols: opts.protocols ?? [] },
            options.openTimeoutMs ?? 15_000,
        );
        const chosen = (readJson(answer) as Partial<WsOpenResponse> | undefined)?.protocol;
        channel.opened(typeof chosen === 'string' ? chosen : '');

        return channel.api;
    }

    const pending =
        (operation: string) =>
        async () : Promise<never> => {
            throw new Error(`bridge: ${operation} is not implemented yet`);
        };

    return {
        net: {
            async browse(type, options) {
                const timeoutMs = Math.min(options?.timeoutMs ?? DEFAULT_BROWSE_MS, MAX_BROWSE_MS);

                return (await sendRequest({ op: 'net.browse', type, timeoutMs })).services;
            },
            http: { request: httpRequest },
            ws: { open: openWs },
            tcp: { open: openTcp },
        },
        // Deferred with the secrets work (decision §12).
        vault: {
            set: pending('vault.set'),
            has: pending('vault.has'),
            delete: pending('vault.delete'),
        },
        storage,
        device: {
            // The screen stays on while the app is shown (Phase 3 E3b); full screen is Phase 4.
            async keepAwake(enabled) {
                await sendRequest({ op: 'device.keepAwake', enabled });
            },
            async setBrightness(level) {
                await sendRequest({ op: 'device.setBrightness', level });
            },
            fullscreen: pending('device.fullscreen'),
            info: () => sendRequest({ op: 'device.info' }),
            async watchSensor(sensor, listener) {
                const listeners = sensorListeners[sensor];
                // The sensor runs while anyone listens; the host is told when the first starts and the last stops.
                if (listeners.size === 0) {
                    await sendRequest({ op: 'device.watchSensor', sensor, enabled: true });
                }
                listeners.add(listener);

                return () => {
                    if (!listeners.delete(listener) || listeners.size > 0) {
                        return;
                    }
                    sendRequest({ op: 'device.watchSensor', sensor, enabled: false }).catch((error: unknown) => {
                        log(`bridge: the ${sensor} sensor did not stop: ${error instanceof Error ? error.message : String(error)}`);
                    });
                };
            },
        },
        // The shell's updates of the app (app-updates AU-27); hosts without them refuse.
        app: {
            updateStatus: () => sendRequest({ op: 'app.updateStatus' }),
            checkForUpdates: () => sendRequest({ op: 'app.checkForUpdates' }),
            async applyUpdate() {
                await sendRequest({ op: 'app.applyUpdate' });
            },
            async ready() {
                await sendRequest({ op: 'app.ready' });
            },
            setChannel: (channel) => sendRequest({ op: 'app.setChannel', channel }),
        },
        lifecycle: {
            onForeground(listener) {
                foregroundListeners.add(listener);

                return () => foregroundListeners.delete(listener);
            },
            onBackground(listener) {
                backgroundListeners.add(listener);

                return () => backgroundListeners.delete(listener);
            },
        },
    };
}
